/**
 * @fileoverview Role capability matrix API calls.
 * @description Reads what each Sales role may do and changes it. The matrix read is real: it is
 * composed from three authorization GETs — List Roles (3.5.2.1), List Permissions (3.5.2.7, both
 * ACTIVE and INACTIVE) and, per Sales role, List Role Permissions (3.5.2.9) — each followed
 * page by page, then mapped onto the page's own CapabilityMatrix. A Sales role missing from the
 * role catalogue rejects the load rather than leaving its column out. The backend has no matrix
 * version, "new" flag or custom-change count, so those are left empty (''/false/0). Saving is real
 * too: one PATCH /authorization/role-permissions (Update Role Permissions) grants and revokes
 * several permissions on several roles in a single all-or-nothing request, each role naming the
 * row version it was loaded at as `ifMatch`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { SALES_ROLE_CODES } from '@/features/capabilities/constants/capability-roles.constants'
import { ApiException } from '@/shared/api/api.exception'
import { httpClient } from '@/shared/api/http-client'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

import type {
  CapabilityChange,
  CapabilityGroup,
  CapabilityMatrix,
  CapabilityPermission,
  CapabilityRole,
  SaveCapabilityChangesResponse,
} from '@/features/capabilities/types/capabilities.types'
import type {
  PermissionDto,
  RoleDto,
  RolePermissionDto,
  RolePermissionsOfRoleRequest,
  UpdateRolePermissionsRequest,
  UpdateRolePermissionsResponse,
} from '@/features/capabilities/types/role-capabilities-api.types'
import type { ResponseWithPageMeta } from '@/shared/api/pagination.utils'

const ROLE_PERMISSIONS_PATH = '/authorization/role-permissions'
const ROLES_PATH = '/authorization/roles'
const PERMISSIONS_PATH = '/authorization/permissions'
const PAGE_LIMIT = 100
// A safety stop well above any real catalogue size, so a misbehaving total can't loop forever. Hitting
// it is an error (getAllPages), not a reason to show what was read so far.
const MAX_PAGES = 50

/**
 * @description Reads every page of a paged authorization list. A list is only returned when it is
 * provably complete: each page must carry its `total`, and the pages must add up to it within
 * MAX_PAGES. Anything else is an error, never a shorter list — a role's mappings read as partial
 * would show real grants as "No", and a save made from that view could revoke them.
 * @param {string} path - The list endpoint.
 * @param {Record<string, string>} params - Its filters, beside the paging.
 * @returns {Promise<T[]>} Every item, in the backend's order.
 * @throws {ApiException} When a page has no `total`, or the list is longer than MAX_PAGES pages.
 */
async function getAllPages<T>(path: string, params: Record<string, string>): Promise<T[]> {
  const items: T[] = []

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response: ResponseWithPageMeta<T[]> = await httpClient.get<T[]>(path, {
      params: { ...params, page, limit: PAGE_LIMIT },
    })
    const total = response.pageMeta?.total
    if (total === undefined) {
      throw new ApiException(
        ERROR_CODES.UNKNOWN_ERROR,
        `The ${path} list did not say how many items it has, so it cannot be shown as complete.`,
      )
    }

    items.push(...response.data)
    if (response.data.length === 0 || items.length >= total) {
      return items
    }
  }

  throw new ApiException(
    ERROR_CODES.UNKNOWN_ERROR,
    `The ${path} list is longer than ${MAX_PAGES * PAGE_LIMIT} items, so it cannot be shown as complete.`,
  )
}

/**
 * @description Picks the Sales roles out of the role catalogue, in the matrix's column order. A
 * Sales role the catalogue lacks (missing or inactive) is an error, not a column to leave out: a
 * matrix without it would look complete while hiding a role the admin expects to manage.
 * @param {RoleDto[]} roles - Every active role.
 * @returns {RoleDto[]} The Sales roles, one per code in SALES_ROLE_CODES.
 * @throws {ApiException} When a Sales role is not in the catalogue.
 */
function pickSalesRoles(roles: RoleDto[]): RoleDto[] {
  return SALES_ROLE_CODES.map((code) => {
    const role = roles.find((candidate) => candidate.roleCode === code)
    if (!role) {
      throw new ApiException(
        ERROR_CODES.UNKNOWN_ERROR,
        `The ${code} role is missing from the role catalogue.`,
      )
    }

    return role
  })
}

/**
 * @description The module a permission is listed under: the first segment of its code, as in
 * USER.INVITE -> USER and AUTHORIZATION.ROLE.READ -> AUTHORIZATION.
 * @param {string} permissionCode - The permission code.
 * @returns {string} The group key.
 */
function groupKeyOf(permissionCode: string): string {
  return permissionCode.split('.')[0] ?? permissionCode
}

/**
 * @description Composes the page's matrix from the three API reads.
 * @param {RoleDto[]} roles - The Sales roles, in column order.
 * @param {PermissionDto[]} permissions - The permission catalogue.
 * @param {RolePermissionDto[][]} mappings - Each role's active mappings, in the same order as roles.
 * @returns {CapabilityMatrix} Roles as columns, permissions grouped by module as rows.
 */
function composeMatrix(
  roles: RoleDto[],
  permissions: PermissionDto[],
  mappings: RolePermissionDto[][],
): CapabilityMatrix {
  const columns: CapabilityRole[] = roles.map((role) => ({
    id: role.roleId,
    code: role.roleCode,
    name: role.roleName,
    version: role.rowVersion,
    locked: role.isProtected,
  }))
  const held = roles.map(
    (_role, index) =>
      new Set((mappings[index] ?? []).map((mapping) => mapping.permission.permissionCode)),
  )

  const byGroup = new Map<string, CapabilityPermission[]>()
  permissions.forEach((permission) => {
    const grants: Record<string, boolean> = {}
    columns.forEach((column, index) => {
      grants[column.code] = held[index]?.has(permission.permissionCode) ?? false
    })

    const key = groupKeyOf(permission.permissionCode)
    const list = byGroup.get(key) ?? []
    list.push({
      id: permission.permissionId,
      code: permission.permissionCode,
      name: permission.permissionName,
      description: permission.description ?? '',
      active: permission.status === 'ACTIVE',
      sensitive: permission.isSensitive,
      stepUp: permission.requiresStepUpMfa,
      isNew: false,
      grants,
    })
    byGroup.set(key, list)
  })

  const groups: CapabilityGroup[] = [...byGroup.entries()].map(([key, list]) => ({
    key,
    permissions: list,
  }))

  return {
    roles: columns,
    groups,
    version: '',
    counts: { permissions: permissions.length, custom: 0 },
  }
}

/**
 * @description Loads the role/permission matrix from List Roles, List Permissions and each Sales
 * role's List Role Permissions. Any failed read rejects the whole load, so the page shows its
 * error state rather than a matrix with silently missing grants.
 * @returns {Promise<CapabilityMatrix>} The roles, the permissions grouped by module, and who holds
 * what.
 */
export async function getCapabilityMatrix(): Promise<CapabilityMatrix> {
  const [allRoles, activePermissions, inactivePermissions] = await Promise.all([
    getAllPages<RoleDto>(ROLES_PATH, { status: 'ACTIVE' }),
    getAllPages<PermissionDto>(PERMISSIONS_PATH, { status: 'ACTIVE' }),
    getAllPages<PermissionDto>(PERMISSIONS_PATH, { status: 'INACTIVE' }),
  ])

  const roles = pickSalesRoles(allRoles)
  const mappings = await Promise.all(
    roles.map((role) =>
      getAllPages<RolePermissionDto>(
        `${ROLES_PATH}/${encodeURIComponent(role.roleId)}/permissions`,
        {
          status: 'ACTIVE',
        },
      ),
    ),
  )

  return composeMatrix(
    roles,
    [...activePermissions, ...inactivePermissions].sort((a, b) =>
      a.permissionCode.localeCompare(b.permissionCode),
    ),
    mappings,
  )
}

/**
 * @description Groups a save's changes by role into Update Role Permissions' body, in the order the
 * roles first appear. Each role's `ifMatch` is its row version as loaded, as a strong ETag.
 * @param {CapabilityChange[]} changes - What to change, one entry per role/permission pair.
 * @returns {UpdateRolePermissionsRequest} The request body.
 */
function toUpdateRequest(changes: CapabilityChange[]): UpdateRolePermissionsRequest {
  const byRole = new Map<string, RolePermissionsOfRoleRequest>()

  changes.forEach((change) => {
    const role = byRole.get(change.roleId) ?? {
      roleId: change.roleId,
      ifMatch: `"${change.roleVersion}"`,
      permissions: [],
    }
    role.permissions.push({ permissionId: change.permissionId, granted: change.granted })
    byRole.set(change.roleId, role)
  })

  return { roles: [...byRole.values()] }
}

/**
 * @description Saves a batch of grants and revokes in one request: every change lands or none does.
 * A role someone else changed since the page loaded is refused (412) rather than overwritten.
 * @param {CapabilityChange[]} changes - What to change, one entry per role/permission pair.
 * @returns {Promise<SaveCapabilityChangesResponse>} How many changes took effect.
 */
export async function saveCapabilityChanges(
  changes: CapabilityChange[],
): Promise<SaveCapabilityChangesResponse> {
  if (changes.length === 0) {
    return { saved: 0 }
  }

  const { data } = await httpClient.patch<UpdateRolePermissionsResponse>(
    ROLE_PERMISSIONS_PATH,
    toUpdateRequest(changes),
  )
  return { saved: data.changedCount }
}
